import { useEffect, useState, type DragEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import {
  AlignLeft,
  ArrowDown,
  ArrowUp,
  BedDouble,
  CalendarDays,
  CheckCircle2,
  Eye,
  EyeOff,
  FileText,
  Gift,
  GripVertical,
  Hash,
  Home,
  Info,
  LayoutList,
  List,
  ListChecks,
  Lock,
  Mail,
  MessageSquare,
  PawPrint,
  Pill,
  Plus,
  Receipt,
  RotateCcw,
  Save,
  Send,
  ShieldCheck,
  Sparkles,
  StickyNote,
  ToggleLeft,
  Trash2,
  Type,
  Undo2,
  Utensils,
  X,
  type LucideIcon,
} from "lucide-react";

import { NieuweReserveringWizard } from "../pensionreservering/NieuweReserveringWizard";
import { TekstBlok, VraagVeld } from "../pensionreservering/EigenElementen";
import {
  KERN,
  VERPLICHTE_BLOKKEN,
  VRAAGTYPES,
  blokTitel,
  blokUitleg,
  indelingFout,
  magStapVerplaatsen,
  nieuweId,
  stapKop,
  stapNaam,
  type Indeling,
  type StapSoort,
  type VraagType,
  type WizardElement,
  type WizardStap,
} from "../pensionreservering/indeling";
import "../pensionreservering/NieuweReserveringWizard.css";
import "./PensionWizardIndeling.css";

// settings-pension: "Wizard Nieuwe reservering" — de beheerder deelt de wizard in: stappen verschuiven, aan- en
// uitzetten en toevoegen; per stap de vaste onderdelen verschuiven, hernoemen en verbergen, en eigen vragen en
// tekstblokken toevoegen. Hij ziet de stap zoals de balie hem krijgt, en kan de hele wizard als voorbeeld doorlopen.
// Wijzigingen zijn een concept tot hij publiceert. De api (ROLE_PENSION_ADMIN) controleert dezelfde regels.

interface Beheer {
  vestigingId: number;
  vestigingCode: string | null;
  concept: Indeling;
  conceptGewijzigd: boolean;
  conceptGewijzigdOp: string | null;
  versie: number;
  gepubliceerdOp: string | null;
  standaard: Indeling;
}

type Vertaal = ReturnType<typeof useTranslate>;

const SLEEP_STAP = "application/x-wizard-stap";
const SLEEP_ELEMENT = "application/x-wizard-element";
const SLEEP_NIEUW = "application/x-wizard-nieuw";

const BLOK_ICOON: Record<string, LucideIcon> = {
  EMAIL: Mail,
  ADRES: Home,
  NIEUW_DIER: Plus,
  BESCHIKBAARHEID: CheckCircle2,
  TYPE_VERBLIJF: BedDouble,
  VOEDING: Utensils,
  MEDICATIE: Pill,
  VERZORGING: Sparkles,
  BIJZONDERHEDEN: StickyNote,
  ARRANGEMENTEN: Gift,
  VERZEKERING: ShieldCheck,
  WENSEN: MessageSquare,
  NOTITIE: FileText,
  INFO: Info,
  SAMENVATTING: Receipt,
  CONCEPT: Save,
};

const VRAAG_ICOON: Record<VraagType, LucideIcon> = {
  TEXT: Type,
  TEXTAREA: AlignLeft,
  NUMBER: Hash,
  BOOLEAN: ToggleLeft,
  DATE: CalendarDays,
  SELECT: List,
  MULTISELECT: ListChecks,
};

const klantstap = (s: WizardStap) => s.soort === "ZOEKEN" || s.soort === "KIEZEN";

function datumTijd(iso: string | null, locale: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(d);
}

function foutReden(e: unknown): string {
  const x = e as { message?: string; response?: { data?: { message?: string; reason?: string } } } | null;
  return x?.response?.data?.reason ?? x?.response?.data?.message ?? x?.message ?? "";
}

export function PensionWizardIndeling({ vestigingId, onBack }: { vestigingId: number; onBack: () => void }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const url = `pension/wizard-indeling?vestigingId=${vestigingId}`;
  const query = useCustom<Beheer>({ url, method: "get", queryOptions: { retry: false } });
  const { mutateAsync, isLoading: bezig } = useCustomMutation<Beheer>();
  const [beheer, setBeheer] = useState<Beheer | null>(null);
  const [indeling, setIndeling] = useState<Indeling | null>(null);
  const [vuil, setVuil] = useState(false);
  const [stapId, setStapId] = useState<string | null>(null);
  const [elementId, setElementId] = useState<string | null>(null);
  const [melding, setMelding] = useState<string | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [voorbeeld, setVoorbeeld] = useState(false);
  const [sluitVraag, setSluitVraag] = useState(false);

  useEffect(() => {
    const data = query.data?.data;
    if (data && !beheer) {
      setBeheer(data);
      setIndeling(data.concept);
      setStapId(data.concept.stappen[0]?.id ?? null);
    }
  }, [query.data, beheer]);

  const sluit = () => (vuil ? setSluitVraag(true) : onBack());

  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !voorbeeld) sluit();
    };
    document.addEventListener("keydown", toets);
    return () => document.removeEventListener("keydown", toets);
  });

  const status = (query.error as { statusCode?: number } | null)?.statusCode;

  const overlay = (inhoud: ReactNode) =>
    createPortal(
      <div className="wi-overlay">
        <div className="wi" role="dialog" aria-modal="true" aria-labelledby="wi-titel">
          {inhoud}
        </div>
      </div>,
      document.body,
    );

  if (!indeling || !beheer) {
    return overlay(
      <>
        <header className="wi__kop">
          <span className="wi__icoon" aria-hidden="true">
            <LayoutList size={24} />
          </span>
          <div className="wi__titels">
            <h2 id="wi-titel">{t("pensionWizard.titel")}</h2>
          </div>
          <button type="button" className="nr__kopknop" aria-label={t("pensionWizard.knop.sluiten")} onClick={onBack}>
            <X size={21} aria-hidden="true" />
          </button>
        </header>
        <p className={query.isError ? "wi-fout" : "wi-leeg"} role={query.isError ? "alert" : undefined}>
          {query.isError ? t(status === 403 ? "pensionWizard.fout.alleenBeheer" : "pensionWizard.fout.laden") : t("common.loading")}
        </p>
      </>,
    );
  }

  const stappen = indeling.stappen;
  const plek = Math.max(0, stappen.findIndex((s) => s.id === stapId));
  const stap = stappen[plek];
  const element = stap.elementen.find((e) => e.id === elementId) ?? null;
  const dierenPlek = stappen.findIndex((s) => s.soort === "DIEREN");
  const regel = indelingFout(indeling);

  // ── Wijzigen ─────────────────────────────────────────────────────────────────────────────────────
  const wijzig = (f: (s: WizardStap[]) => WizardStap[]) => {
    setIndeling((i) => (i ? { stappen: f(i.stappen) } : i));
    setVuil(true);
    setMelding(null);
    setFout(null);
  };
  const zetStap = (id: string, deel: Partial<WizardStap>) => wijzig((ss) => ss.map((s) => (s.id === id ? { ...s, ...deel } : s)));
  const zetElementen = (id: string, f: (e: WizardElement[]) => WizardElement[]) =>
    wijzig((ss) => ss.map((s) => (s.id === id ? { ...s, elementen: f(s.elementen) } : s)));
  const zetElement = (id: string, deel: Partial<WizardElement>) =>
    zetElementen(stap.id, (es) => es.map((e) => (e.id === id ? { ...e, ...deel } : e)));

  const verplaatsStap = (van: number, naar: number) => {
    if (!magStapVerplaatsen(stappen, van, naar)) return;
    wijzig((ss) => {
      const kopie = [...ss];
      const [x] = kopie.splice(van, 1);
      kopie.splice(naar, 0, x);
      return kopie;
    });
  };
  const verplaatsElement = (van: number, naar: number) => {
    if (naar < 0 || naar >= stap.elementen.length || van === naar) return;
    zetElementen(stap.id, (es) => {
      const kopie = [...es];
      const [x] = kopie.splice(van, 1);
      kopie.splice(naar, 0, x);
      return kopie;
    });
  };
  const nieuwElement = (soort: "TEKST" | VraagType, voor?: number) => {
    const id = nieuweId(indeling, soort === "TEKST" ? "tekst" : "vraag");
    const e: WizardElement =
      soort === "TEKST"
        ? { id, soort: "TEKST", blok: null, titel: t("pensionWizard.nieuw.tekst"), uitleg: null, actief: true, verplicht: false, type: null, opties: null, perDier: false }
        : {
            id,
            soort: "VRAAG",
            blok: null,
            titel: t("pensionWizard.nieuw.vraag"),
            uitleg: null,
            actief: true,
            verplicht: false,
            type: soort,
            opties: soort === "SELECT" || soort === "MULTISELECT" ? [1, 2].map((n) => t("pensionWizard.nieuw.optie", { args: [n] })) : null,
            perDier: false,
          };
    zetElementen(stap.id, (es) => {
      const kopie = [...es];
      kopie.splice(voor ?? kopie.length, 0, e);
      return kopie;
    });
    setElementId(id);
  };
  const naarAndereStap = (elId: string, vanStap: string, naarStap: number) => {
    const doel = stappen[naarStap];
    const bron = stappen.find((s) => s.id === vanStap);
    const el = bron?.elementen.find((e) => e.id === elId);
    if (!doel || !bron || !el || el.soort === "BLOK" || doel.id === bron.id) return;
    if (el.soort === "VRAAG" && klantstap(doel)) return;
    const verplaatst = { ...el, perDier: el.perDier && naarStap > dierenPlek };
    wijzig((ss) =>
      ss.map((s) =>
        s.id === bron.id
          ? { ...s, elementen: s.elementen.filter((e) => e.id !== elId) }
          : s.id === doel.id
            ? { ...s, elementen: [...s.elementen, verplaatst] }
            : s,
      ),
    );
    setStapId(doel.id);
    setElementId(elId);
  };
  const nieuweStap = () => {
    const id = nieuweId(indeling, "stap");
    const controle = stappen.findIndex((s) => s.soort === "CONTROLE");
    wijzig((ss) => {
      const kopie = [...ss];
      kopie.splice(controle, 0, { id, soort: "EIGEN", titel: t("pensionWizard.nieuw.stap"), uitleg: null, actief: true, elementen: [] });
      return kopie;
    });
    setStapId(id);
    setElementId(null);
  };

  // ── Opslaan en publiceren ────────────────────────────────────────────────────────────────────────
  const verstuur = async (soort: "opslaan" | "publiceren" | "verwerpen" | "standaard") => {
    setFout(null);
    setMelding(null);
    try {
      let res: Beheer | null = null;
      if (soort === "opslaan" || (soort === "publiceren" && vuil)) {
        res = (
          await mutateAsync({ url: `pension/wizard-indeling/concept?vestigingId=${vestigingId}`, method: "put", values: indeling, successNotification: false, errorNotification: false })
        ).data;
      }
      if (soort === "publiceren") {
        res = (
          await mutateAsync({ url: `pension/wizard-indeling/publiceren?vestigingId=${vestigingId}`, method: "post", values: {}, successNotification: false, errorNotification: false })
        ).data;
      } else if (soort === "verwerpen") {
        res = (
          await mutateAsync({ url: `pension/wizard-indeling/concept?vestigingId=${vestigingId}`, method: "delete", values: {}, successNotification: false, errorNotification: false })
        ).data;
      } else if (soort === "standaard") {
        res = (
          await mutateAsync({ url: `pension/wizard-indeling/standaard?vestigingId=${vestigingId}`, method: "post", values: {}, successNotification: false, errorNotification: false })
        ).data;
      }
      if (res) {
        setBeheer(res);
        setIndeling(res.concept);
        if (!res.concept.stappen.some((s) => s.id === stapId)) setStapId(res.concept.stappen[0]?.id ?? null);
        setElementId(null);
      }
      setVuil(false);
      setMelding(
        soort === "publiceren"
          ? t("pensionWizard.melding.gepubliceerd", { args: [res?.versie ?? ""] })
          : t(`pensionWizard.melding.${soort === "opslaan" ? "opgeslagen" : soort === "verwerpen" ? "verworpen" : "standaard"}`),
      );
    } catch (e) {
      setFout(t("pensionWizard.fout.opslaan", { args: [foutReden(e)] }));
    }
  };

  const conceptOpen = vuil || beheer.conceptGewijzigd;

  // ── Slepen ───────────────────────────────────────────────────────────────────────────────────────
  const opStap = (i: number) => (e: DragEvent) => {
    e.preventDefault();
    const van = e.dataTransfer.getData(SLEEP_STAP);
    if (van !== "") return verplaatsStap(Number(van), i);
    const el = e.dataTransfer.getData(SLEEP_ELEMENT);
    if (el) {
      const [bron, id] = el.split("|");
      naarAndereStap(id, bron, i);
    }
  };
  const opElement = (i: number) => (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const nieuw = e.dataTransfer.getData(SLEEP_NIEUW);
    if (nieuw) return nieuwElement(nieuw as "TEKST" | VraagType, i);
    const el = e.dataTransfer.getData(SLEEP_ELEMENT);
    if (el) {
      const [bron, id] = el.split("|");
      if (bron !== stap.id) return;
      const van = stap.elementen.findIndex((x) => x.id === id);
      verplaatsElement(van, van < i ? i - 1 : i);
    }
  };
  const toelaten = (e: DragEvent) => e.preventDefault();

  const kop = stap.soort === "EIGEN" ? { titel: "", uitleg: "" } : stapKop(t as never, { ...stap, titel: null, uitleg: null }, stap.soort.toLowerCase());
  const naamStandaard = stap.soort === "EIGEN" ? "" : stapNaam(t as never, { ...stap, titel: null });

  return overlay(
    <>
      <header className="wi__kop">
        <span className="wi__icoon" aria-hidden="true">
          <LayoutList size={24} />
        </span>
        <div className="wi__titels">
          <h2 id="wi-titel">{t("pensionWizard.titel")}</h2>
          <p>
            {beheer.versie > 0
              ? t("pensionWizard.status.versie", { args: [beheer.versie, datumTijd(beheer.gepubliceerdOp, locale)] })
              : t("pensionWizard.status.standaard")}
            {conceptOpen && (
              <span className="nr-chip" data-toon="accent">
                {vuil ? t("pensionWizard.status.nietOpgeslagen") : t("pensionWizard.status.concept")}
              </span>
            )}
          </p>
        </div>
        <div className="wi__knoppen">
          <button type="button" className="nr-knop nr-knop--rand" onClick={() => setVoorbeeld(true)} disabled={!!regel}>
            <Eye size={16} aria-hidden="true" />
            {t("pensionWizard.knop.voorbeeld")}
          </button>
          <button type="button" className="nr-knop nr-knop--rand" onClick={() => void verstuur("standaard")} disabled={bezig}>
            <RotateCcw size={16} aria-hidden="true" />
            {t("pensionWizard.knop.standaard")}
          </button>
          {beheer.conceptGewijzigd && (
            <button type="button" className="nr-knop nr-knop--rand" onClick={() => void verstuur("verwerpen")} disabled={bezig}>
              <Undo2 size={16} aria-hidden="true" />
              {t("pensionWizard.knop.verwerpen")}
            </button>
          )}
          <button type="button" className="nr-knop nr-knop--rand" onClick={() => void verstuur("opslaan")} disabled={bezig || !vuil || !!regel}>
            <Save size={16} aria-hidden="true" />
            {t("pensionWizard.knop.opslaan")}
          </button>
          <button type="button" className="nr-knop nr-knop--hoofd" onClick={() => void verstuur("publiceren")} disabled={bezig || !conceptOpen || !!regel}>
            <Send size={16} aria-hidden="true" />
            {t("pensionWizard.knop.publiceren")}
          </button>
          <button type="button" className="nr__kopknop" aria-label={t("pensionWizard.knop.sluiten")} onClick={sluit}>
            <X size={21} aria-hidden="true" />
          </button>
        </div>
      </header>

      {sluitVraag && (
        <div className="wi-balk" role="alert">
          <span>{t("pensionWizard.sluiten.vraag")}</span>
          <button type="button" className="nr-knop nr-knop--rand" onClick={() => setSluitVraag(false)}>
            {t("pensionWizard.sluiten.terug")}
          </button>
          <button type="button" className="nr-knop nr-knop--hoofd" onClick={onBack}>
            {t("pensionWizard.sluiten.weggooien")}
          </button>
        </div>
      )}
      {regel && (
        <p className="wi-fout" role="alert">
          {t(`pensionWizard.regel.${regel}`)}
        </p>
      )}
      {fout && (
        <p className="wi-fout" role="alert">
          {fout}
        </p>
      )}
      {melding && (
        <p className="wi-melding" role="status">
          {melding}
        </p>
      )}

      <div className="wi__lijf">
        {/* ── De stappen ── */}
        <nav className="wi-stappen" aria-label={t("pensionWizard.stappen")}>
          <h3>{t("pensionWizard.stappen")}</h3>
          <ol>
            {stappen.map((s, i) => {
              const naam = stapNaam(t as never, s) || t("pensionWizard.stap.eigen");
              const vastePlek = s.soort === "ZOEKEN" || s.soort === "KIEZEN" || s.soort === "CONTROLE";
              return (
                <li
                  key={s.id}
                  className="wi-stap"
                  data-gekozen={s.id === stap.id || undefined}
                  data-uit={!s.actief || undefined}
                  draggable={!vastePlek}
                  onDragStart={(e) => e.dataTransfer.setData(SLEEP_STAP, String(i))}
                  onDragOver={toelaten}
                  onDrop={opStap(i)}
                >
                  <button
                    type="button"
                    className="wi-stap__kies"
                    aria-current={s.id === stap.id ? "step" : undefined}
                    onClick={() => {
                      setStapId(s.id);
                      setElementId(null);
                    }}
                  >
                    <span className="wi-stap__nr" aria-hidden="true">
                      {i + 1}
                    </span>
                    <span className="wi-stap__tekst">
                      <strong>{naam}</strong>
                      <span>
                        {!s.actief
                          ? t("pensionWizard.stap.uit")
                          : s.soort === "EIGEN"
                            ? t("pensionWizard.stap.eigen")
                            : vastePlek
                              ? t("pensionWizard.stap.vastePlek")
                              : t("pensionWizard.stap.vast")}
                      </span>
                    </span>
                    {vastePlek && <Lock size={14} aria-hidden="true" className="wi-stap__slot" />}
                  </button>
                  <span className="wi-stap__knoppen">
                    <button
                      type="button"
                      className="wi-mini"
                      aria-label={t("pensionWizard.stap.omhoog", { args: [naam] })}
                      disabled={!magStapVerplaatsen(stappen, i, i - 1)}
                      onClick={() => verplaatsStap(i, i - 1)}
                    >
                      <ArrowUp size={14} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="wi-mini"
                      aria-label={t("pensionWizard.stap.omlaag", { args: [naam] })}
                      disabled={!magStapVerplaatsen(stappen, i, i + 1)}
                      onClick={() => verplaatsStap(i, i + 1)}
                    >
                      <ArrowDown size={14} aria-hidden="true" />
                    </button>
                    {!KERN.includes(s.soort) && (
                      <button
                        type="button"
                        className="wi-mini"
                        aria-label={t(s.actief ? "pensionWizard.stap.verbergen" : "pensionWizard.stap.tonen", { args: [naam] })}
                        aria-pressed={!s.actief}
                        onClick={() => zetStap(s.id, { actief: !s.actief })}
                      >
                        {s.actief ? <Eye size={14} aria-hidden="true" /> : <EyeOff size={14} aria-hidden="true" />}
                      </button>
                    )}
                    {s.soort === "EIGEN" && (
                      <button
                        type="button"
                        className="wi-mini"
                        aria-label={t("pensionWizard.stap.verwijder", { args: [naam] })}
                        onClick={() => {
                          wijzig((ss) => ss.filter((x) => x.id !== s.id));
                          setStapId(stappen[Math.max(0, i - 1)].id);
                        }}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    )}
                  </span>
                </li>
              );
            })}
          </ol>
          <button type="button" className="nr-knop nr-knop--rand wi-stappen__nieuw" onClick={nieuweStap}>
            <Plus size={16} aria-hidden="true" />
            {t("pensionWizard.stap.nieuw")}
          </button>
        </nav>

        {/* ── De stap zoals de balie hem ziet ── */}
        <main className="wi-canvas" aria-label={t("pensionWizard.stap.nr", { args: [plek + 1, stappen.length] })}>
          <div className="wi-canvas__kop">
            <span className="nr-chip">{t("pensionWizard.stap.nr", { args: [plek + 1, stappen.length] })}</span>
            {KERN.includes(stap.soort) && <span className="nr-chip" data-toon="groen">{t("pensionWizard.stap.nodig")}</span>}
            {!stap.actief && <span className="nr-chip" data-toon="rood">{t("pensionWizard.stap.uit")}</span>}
          </div>
          <input
            className="wi-kopveld"
            aria-label={t("pensionWizard.stap.naam")}
            placeholder={kop.titel || t("pensionWizard.stap.naam")}
            maxLength={80}
            value={stap.titel ?? ""}
            onChange={(e) => zetStap(stap.id, { titel: e.target.value === "" ? null : e.target.value })}
          />
          <textarea
            className="wi-uitlegveld"
            aria-label={t("pensionWizard.stap.uitlegVeld")}
            placeholder={kop.uitleg || t("pensionWizard.stap.uitlegVeld")}
            maxLength={500}
            rows={2}
            value={stap.uitleg ?? ""}
            onChange={(e) => zetStap(stap.id, { uitleg: e.target.value === "" ? null : e.target.value })}
          />
          {naamStandaard && stap.titel && <p className="wi-hint">{t("pensionWizard.stap.standaardNaam", { args: [naamStandaard] })}</p>}
          <p className="wi-hint">{t(`pensionWizard.stapUitleg.${stap.soort}`)}</p>

          <ul className="wi-elementen" onDragOver={toelaten} onDrop={opElement(stap.elementen.length)}>
            {stap.elementen.map((e, i) => {
              const naam = elementNaam(t, stap.soort, e);
              const nodig = e.soort === "BLOK" && VERPLICHTE_BLOKKEN.includes(e.blok ?? "");
              return (
                <li
                  key={e.id}
                  className="wi-element"
                  data-gekozen={e.id === elementId || undefined}
                  data-uit={!e.actief || undefined}
                  draggable
                  onDragStart={(ev) => {
                    ev.stopPropagation();
                    ev.dataTransfer.setData(SLEEP_ELEMENT, `${stap.id}|${e.id}`);
                  }}
                  onDragOver={toelaten}
                  onDrop={opElement(i)}
                >
                  <span className="wi-element__greep" aria-hidden="true">
                    <GripVertical size={18} />
                  </span>
                  <button
                    type="button"
                    className="wi-element__voorbeeld"
                    aria-label={t("pensionWizard.element.kies", { args: [naam] })}
                    aria-pressed={e.id === elementId}
                    onClick={() => setElementId(e.id)}
                  >
                    <ElementVoorbeeld stap={stap.soort} e={e} t={t} />
                  </button>
                  <span className="wi-element__knoppen">
                    <button type="button" className="wi-mini" aria-label={t("pensionWizard.element.omhoog", { args: [naam] })} disabled={i === 0} onClick={() => verplaatsElement(i, i - 1)}>
                      <ArrowUp size={14} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="wi-mini"
                      aria-label={t("pensionWizard.element.omlaag", { args: [naam] })}
                      disabled={i === stap.elementen.length - 1}
                      onClick={() => verplaatsElement(i, i + 1)}
                    >
                      <ArrowDown size={14} aria-hidden="true" />
                    </button>
                    {!nodig && (
                      <button
                        type="button"
                        className="wi-mini"
                        aria-label={t(e.actief ? "pensionWizard.element.verbergen" : "pensionWizard.element.tonen", { args: [naam] })}
                        aria-pressed={!e.actief}
                        onClick={() => zetElement(e.id, { actief: !e.actief })}
                      >
                        {e.actief ? <Eye size={14} aria-hidden="true" /> : <EyeOff size={14} aria-hidden="true" />}
                      </button>
                    )}
                    {e.soort !== "BLOK" && (
                      <button
                        type="button"
                        className="wi-mini"
                        aria-label={t("pensionWizard.element.verwijder", { args: [naam] })}
                        onClick={() => {
                          zetElementen(stap.id, (es) => es.filter((x) => x.id !== e.id));
                          setElementId(null);
                        }}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                      </button>
                    )}
                  </span>
                </li>
              );
            })}
            <li className="wi-elementen__leeg" aria-hidden="true">
              {t("pensionWizard.element.leeg")}
            </li>
          </ul>
        </main>

        {/* ── Toevoegen en eigenschappen ── */}
        <aside className="wi-paneel" aria-label={t("pensionWizard.eigenschappen")}>
          <section className="wi-paneel__deel">
            <h3>{t("pensionWizard.toevoegen")}</h3>
            <p className="wi-hint">{klantstap(stap) ? t("pensionWizard.toevoegenKlant") : t("pensionWizard.toevoegenUitleg")}</p>
            <div className="wi-palet">
              {(["TEKST", ...(klantstap(stap) ? [] : VRAAGTYPES)] as ("TEKST" | VraagType)[]).map((soort) => {
                const Icoon = soort === "TEKST" ? Info : VRAAG_ICOON[soort];
                return (
                  <button
                    key={soort}
                    type="button"
                    className="wi-palet__knop"
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData(SLEEP_NIEUW, soort)}
                    onClick={() => nieuwElement(soort)}
                  >
                    <Icoon size={16} aria-hidden="true" />
                    {t(`pensionWizard.soort.${soort}`)}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="wi-paneel__deel">
            <h3>{t("pensionWizard.eigenschappen")}</h3>
            {!element ? (
              <>
                <p className="wi-hint">{t("pensionWizard.eigenschappen.leeg")}</p>
                {KERN.includes(stap.soort) ? (
                  <p className="wi-hint">{t("pensionWizard.stap.kern")}</p>
                ) : (
                  <label className="nr-vink">
                    <input type="checkbox" checked={stap.actief} onChange={(e) => zetStap(stap.id, { actief: e.target.checked })} />
                    {t("pensionWizard.stap.aan")}
                  </label>
                )}
              </>
            ) : (
              <Eigenschappen
                key={element.id}
                stap={stap}
                e={element}
                t={t}
                perDierMag={plek > dierenPlek}
                onZet={(deel) => zetElement(element.id, deel)}
                onVerwijder={() => {
                  zetElementen(stap.id, (es) => es.filter((x) => x.id !== element.id));
                  setElementId(null);
                }}
              />
            )}
          </section>
        </aside>
      </div>

      {voorbeeld && (
        <NieuweReserveringWizard voorbeeld indeling={{ versie: beheer.versie, indeling }} onSluit={() => setVoorbeeld(false)} />
      )}
    </>,
  );
}

function elementNaam(t: Vertaal, stap: StapSoort, e: WizardElement): string {
  if (e.soort === "BLOK") return blokTitel(t as never, stap, e) || t(`pensionWizard.blok.${e.blok}`);
  return e.titel || t(`pensionWizard.soort.${e.soort === "TEKST" ? "TEKST" : e.type}`);
}

/** Een onderdeel zoals het er in de wizard ongeveer uitziet, om te herkennen wat je verschuift. */
function ElementVoorbeeld({ stap, e, t }: { stap: StapSoort; e: WizardElement; t: Vertaal }) {
  const labels = (
    <span className="wi-labels">
      {e.soort === "BLOK" && <span className="nr-chip">{t("pensionWizard.element.vast")}</span>}
      {e.soort === "VRAAG" && <span className="nr-chip">{t(`pensionWizard.soort.${e.type}`)}</span>}
      {e.verplicht && <span className="nr-chip" data-toon="groen">{t("pensionWizard.element.verplicht")}</span>}
      {e.perDier && (
        <span className="nr-chip" data-toon="accent">
          <PawPrint size={12} aria-hidden="true" /> {t("pensionWizard.element.perDier")}
        </span>
      )}
      {!e.actief && <span className="nr-chip" data-toon="rood">{t("pensionWizard.element.uit")}</span>}
    </span>
  );
  if (e.soort === "TEKST") {
    return (
      <span className="wi-voorbeeld">
        {labels}
        <TekstBlok titel={e.titel} tekst={e.uitleg} />
      </span>
    );
  }
  if (e.soort === "VRAAG") {
    return (
      <span className="wi-voorbeeld">
        {labels}
        <VraagVeld vraag={e} waarde="" onChange={() => {}} t={t as never} uit />
      </span>
    );
  }
  const Icoon = BLOK_ICOON[e.blok ?? ""] ?? Info;
  const titel = blokTitel(t as never, stap, e);
  const uitleg = blokUitleg(t as never, stap, e);
  const nep = (inhoud: ReactNode) => <span className="wi-nep">{inhoud}</span>;
  let skelet: ReactNode = null;
  switch (e.blok) {
    case "EMAIL":
    case "ADRES":
      skelet = nep(<span className="wi-nep__veld">{e.uitleg ?? t(e.blok === "EMAIL" ? "nieuweReservering.zoeken.emailPlaats" : "nieuweReservering.zoeken.adresPlaats")}</span>);
      break;
    case "NIEUW_DIER":
    case "CONCEPT":
      skelet = nep(<span className="wi-nep__knop">{titel}</span>);
      break;
    case "BESCHIKBAARHEID":
      skelet = nep(<span className="nr-chip" data-toon="groen">{t("nieuweReservering.periode.ok")}</span>);
      break;
    case "TYPE_VERBLIJF":
      skelet = nep(<span className="wi-nep__veld">{t("nieuweReservering.verblijf.kiesType")}</span>);
      break;
    case "VOEDING":
      skelet = nep(
        <>
          <span className="wi-nep__veld">{t("nieuweReservering.verblijf.voedingSTANDAARD")}</span>
          <span className="wi-nep__veld wi-nep__veld--hoog">{t("nieuweReservering.verblijf.voedingPlaats")}</span>
        </>,
      );
      break;
    case "MEDICATIE":
      skelet = nep(<span className="wi-nep__veld">{t("nieuweReservering.verblijf.medicatieNodig")}</span>);
      break;
    case "VERZORGING":
      skelet = nep(
        <>
          <span className="wi-nep__veld">{t("nieuweReservering.verblijf.borstelen")}</span>
          <span className="wi-nep__veld">{t("nieuweReservering.verblijf.wassen")}</span>
          <span className="wi-nep__veld">{t("nieuweReservering.verblijf.samen")}</span>
        </>,
      );
      break;
    case "BIJZONDERHEDEN":
    case "WENSEN":
    case "NOTITIE":
      skelet = nep(<span className="wi-nep__veld wi-nep__veld--hoog" />);
      break;
    case "ARRANGEMENTEN":
      skelet = nep(
        <>
          <span className="wi-nep__kaart" />
          <span className="wi-nep__kaart" />
          <span className="wi-nep__kaart" />
        </>,
      );
      break;
    case "VERZEKERING":
      skelet = nep(
        <>
          <span className="wi-nep__veld">{t("nieuweReservering.keuzes.verzekeringJa")}</span>
          <span className="wi-nep__veld">{t("nieuweReservering.keuzes.verzekeringNee")}</span>
        </>,
      );
      break;
    case "SAMENVATTING":
      skelet = nep(<span className="wi-nep__veld">{t("nieuweReservering.som.totaal")}</span>);
      break;
    default:
      break;
  }
  if (e.blok === "INFO") {
    return (
      <span className="wi-voorbeeld">
        {labels}
        <TekstBlok titel={e.titel} tekst={uitleg} />
      </span>
    );
  }
  return (
    <span className="wi-voorbeeld wi-blok">
      <span className="nr-rond" aria-hidden="true">
        <Icoon size={20} />
      </span>
      <span className="wi-blok__tekst">
        <strong>{titel}</strong>
        <span className="nr-klein">{uitleg || t(`pensionWizard.blok.${e.blok}`)}</span>
        {skelet}
      </span>
      {labels}
    </span>
  );
}

/** Wat van een onderdeel in te stellen is. */
function Eigenschappen({
  stap,
  e,
  t,
  perDierMag,
  onZet,
  onVerwijder,
}: {
  stap: WizardStap;
  e: WizardElement;
  t: Vertaal;
  perDierMag: boolean;
  onZet: (deel: Partial<WizardElement>) => void;
  onVerwijder: () => void;
}) {
  const nodig = e.soort === "BLOK" && VERPLICHTE_BLOKKEN.includes(e.blok ?? "");
  const keuzes = e.type === "SELECT" || e.type === "MULTISELECT";
  const tekst = (v: string) => (v === "" ? null : v);
  const standaardNaam = e.soort === "BLOK" ? blokTitel(t as never, stap.soort, { ...e, titel: null }) : "";
  const standaardUitleg = e.soort === "BLOK" ? blokUitleg(t as never, stap.soort, { ...e, uitleg: null }) : "";
  const uitlegLabel =
    e.soort === "TEKST" || e.blok === "INFO"
      ? t("pensionWizard.veld.tekst")
      : e.blok === "EMAIL" || e.blok === "ADRES" || e.blok === "BIJZONDERHEDEN"
        ? t("pensionWizard.veld.plaatshouder")
        : t("pensionWizard.veld.uitleg");

  return (
    <div className="wi-eigenschappen">
      <p className="wi-eigenschappen__soort">
        {e.soort === "BLOK" ? t(`pensionWizard.blok.${e.blok}`) : t(`pensionWizard.soort.${e.soort === "TEKST" ? "TEKST" : e.type}`)}
      </p>
      {e.blok !== "INFO" && (
        <label className="nr-veld">
          <span className="nr-veld__label">{e.soort === "VRAAG" ? t("pensionWizard.veld.vraag") : t("pensionWizard.veld.naam")}</span>
          <input
            className="nr-invoer"
            maxLength={80}
            placeholder={standaardNaam || undefined}
            value={e.titel ?? ""}
            onChange={(ev) => onZet({ titel: tekst(ev.target.value) })}
          />
          {e.soort === "BLOK" && <span className="nr-klein">{t("pensionWizard.veld.naamStandaard")}</span>}
        </label>
      )}
      {e.blok === "INFO" && (
        <label className="nr-veld">
          <span className="nr-veld__label">{t("pensionWizard.veld.titel")}</span>
          <input className="nr-invoer" maxLength={80} value={e.titel ?? ""} onChange={(ev) => onZet({ titel: tekst(ev.target.value) })} />
        </label>
      )}
      {!["NIEUW_DIER", "CONCEPT", "SAMENVATTING"].includes(e.blok ?? "") && (
        <label className="nr-veld">
          <span className="nr-veld__label">{uitlegLabel}</span>
          <textarea
            className="nr-invoer"
            maxLength={500}
            rows={3}
            placeholder={standaardUitleg || undefined}
            value={e.uitleg ?? ""}
            onChange={(ev) => onZet({ uitleg: tekst(ev.target.value) })}
          />
        </label>
      )}
      {e.soort === "VRAAG" && (
        <>
          <label className="nr-veld">
            <span className="nr-veld__label">{t("pensionWizard.veld.soort")}</span>
            <select
              className="nr-invoer"
              value={e.type ?? "TEXT"}
              onChange={(ev) => {
                const type = ev.target.value as VraagType;
                const metKeuzes = type === "SELECT" || type === "MULTISELECT";
                onZet({
                  type,
                  opties: metKeuzes ? (e.opties?.length ? e.opties : [1, 2].map((n) => t("pensionWizard.nieuw.optie", { args: [n] }))) : null,
                });
              }}
            >
              {VRAAGTYPES.map((v) => (
                <option key={v} value={v}>
                  {t(`pensionWizard.soort.${v}`)}
                </option>
              ))}
            </select>
          </label>
          {keuzes && (
            <label className="nr-veld">
              <span className="nr-veld__label">{t("pensionWizard.veld.keuzes")}</span>
              <textarea
                className="nr-invoer"
                rows={4}
                value={(e.opties ?? []).join("\n")}
                onChange={(ev) => onZet({ opties: ev.target.value.split("\n") })}
              />
            </label>
          )}
          <label className="nr-vink">
            <input type="checkbox" checked={e.verplicht} onChange={(ev) => onZet({ verplicht: ev.target.checked })} />
            {t("pensionWizard.veld.verplicht")}
          </label>
          <label className="nr-vink">
            <input type="checkbox" checked={e.perDier} disabled={!perDierMag} onChange={(ev) => onZet({ perDier: ev.target.checked })} />
            {t("pensionWizard.veld.perDier")}
          </label>
          {!perDierMag && <span className="nr-klein">{t("pensionWizard.veld.perDierNiet")}</span>}
        </>
      )}
      {nodig ? (
        <p className="wi-hint">{t("pensionWizard.veld.nodig")}</p>
      ) : (
        <label className="nr-vink">
          <input type="checkbox" checked={e.actief} onChange={(ev) => onZet({ actief: ev.target.checked })} />
          {t("pensionWizard.veld.tonen")}
        </label>
      )}
      {e.soort !== "BLOK" && (
        <button type="button" className="nr-knop nr-knop--rand wi-verwijder" onClick={onVerwijder}>
          <Trash2 size={16} aria-hidden="true" />
          {t("pensionWizard.veld.verwijder")}
        </button>
      )}
    </div>
  );
}
